import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Card, Dropdown } from 'react-bootstrap';
import { BsArrowLeft, BsDownload, BsJournalBookmark, BsPlus, BsTrash } from 'react-icons/bs';
import { FaMask } from 'react-icons/fa';
import PageHeader from 'Utils/PageHeader';
import AppContext from 'Utils/AppContext';
import { getDataSourceNumericId } from 'DataSources/datasourcesApi';
import {
  deleteInventory,
  getMaskingEntityAlgorithmMap,
  getMaskingProviders,
  latestInventoryOf,
  listDataSourceInventories,
  listMaskingAlgorithms,
  updateInventory,
} from 'Secure/api';
import { registerAlgorithmCatalog } from '../MaskingSources/uniquenessWarning';
import InventorySelector, { InventoryNameModal } from 'Secure/InventorySelector';
import { exportInventoryFields } from 'Secure/inventoryExport';
import SourceDetail, { InventoryContextMenu } from './SourceDetail';
import { maskEligibleConnections, providerLabelFor } from './managerUtils';
import './InventoryManager.css';

/**
 * Dedicated inventory page for ONE data source: /inventory-manager/:id (id = connection id).
 * The editor pane is the shared SourceDetail; this host owns the back button, the inventory
 * dropdown (a source can have several named inventories), the page-header actions
 * (Mask / Export / New / Delete inventory), and the label's right-click menu.
 */
function InventorySourcePage() {
  const { connections, setNotification } = useContext(AppContext);
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();
  const connectionId = Number(routeId);
  // A list-page sub-row click hands over the inventory to open; falls back to the latest.
  const navInventoryId = location.state?.inventoryId;

  const [algorithms, setAlgorithms] = useState([]);
  const [entityAlgorithmMap, setEntityAlgorithmMap] = useState({});
  const [providers, setProviders] = useState([]);
  // Inventories of this source. selectedInventoryId: undefined = resolving; null = new
  // unsaved draft; number = saved inventory.
  const [inventories, setInventories] = useState([]);
  const [selectedInventoryId, setSelectedInventoryId] = useState(undefined);
  const [invLoading, setInvLoading] = useState(false);
  // Right-click menu target + a reload signal for the detail pane after out-of-band inventory
  // changes (provider picked via the menu re-derives the detail's recommendations).
  const [ctxMenu, setCtxMenu] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const loadedRef = useRef(false);
  // Live copy of the detail pane's draft (table → field grid) for the export actions —
  // exports reflect what's on screen, including unsaved edits.
  const exportDraftRef = useRef({});

  const source = useMemo(
    () =>
      Number.isFinite(connectionId)
        ? maskEligibleConnections(connections).find((c) => c.id === connectionId) || null
        : null,
    [connections, connectionId]
  );

  // Algorithm list + entity→algorithm map for recommendations (default provider).
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      const [algoList, map, providerList] = await Promise.all([
        listMaskingAlgorithms().catch(() => []),
        getMaskingEntityAlgorithmMap().catch(() => ({})),
        getMaskingProviders().catch(() => []),
      ]);
      setAlgorithms(algoList);
      registerAlgorithmCatalog(algoList);
      setEntityAlgorithmMap(map && typeof map === 'object' ? map : {});
      setProviders(providerList);
    })();
  }, []);

  const refreshInventories = useCallback(async () => {
    const dsId = getDataSourceNumericId(source);
    if (dsId == null) return [];
    const list = await listDataSourceInventories(dsId).catch(() => []);
    setInventories(list);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source && source.id]);

  // Load this source's inventories; default to the most recently updated one (or a draft
  // when none exist yet).
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setInventories([]);
      setSelectedInventoryId(undefined);
      const dsId = getDataSourceNumericId(source);
      if (dsId == null) return;
      setInvLoading(true);
      const list = await listDataSourceInventories(dsId).catch(() => []);
      if (cancelled) return;
      setInventories(list);
      const navInv =
        navInventoryId != null ? list.find((inv) => inv && inv.id === navInventoryId) : null;
      const latest = latestInventoryOf(list);
      setSelectedInventoryId(navInv ? navInv.id : latest ? latest.id : null);
      setInvLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source && source.id]);

  const openInventoryMenu = useCallback((e, inventory) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY, inventory });
  }, []);

  const handleRenameSubmit = async (name) => {
    const inv = renameTarget;
    setRenameTarget(null);
    if (!inv || inv.id == null) return;
    try {
      await updateInventory(inv.id, { name });
      await refreshInventories();
    } catch (e) {
      setNotification({ header: 'Rename failed', body: e?.message || String(e) });
    }
  };

  const handleSelectProvider = async (inv, serviceId) => {
    if (!inv || inv.id == null) return;
    try {
      // Empty string clears back to the platform default (PUT is partial; blank clears).
      await updateInventory(inv.id, { preferredMaskingProviderId: serviceId || '' });
      await refreshInventories();
      // Re-derive the open editor's recommendations when its own inventory changed provider.
      if (selectedInventoryId === inv.id) setDetailReloadKey((k) => k + 1);
      setNotification({
        header: 'Provider updated',
        body: `${inv.name || `Inventory ${inv.id}`} now uses ${
          serviceId
            ? providerLabelFor({ preferredMaskingProviderId: serviceId }, providers)
            : 'the default provider'
        }.`,
      });
    } catch (e) {
      setNotification({ header: 'Provider update failed', body: e?.message || String(e) });
    }
  };

  const handleDeleteInventory = async (inv) => {
    if (!inv || inv.id == null) return;
    const name = inv.name || `Inventory ${inv.id}`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete inventory "${name}"? This cannot be undone.`)) return;
    try {
      await deleteInventory(inv.id);
      const list = await refreshInventories();
      if (selectedInventoryId === inv.id) {
        const latest = latestInventoryOf(list);
        setSelectedInventoryId(latest ? latest.id : null);
      }
      setNotification({ header: 'Inventory deleted', body: `Deleted inventory "${name}"` });
    } catch (e) {
      setNotification({ header: 'Delete failed', body: e?.message || String(e) });
    }
  };

  // The saved inventory currently open in the detail pane (null while a draft is active).
  const selectedInventory =
    selectedInventoryId != null
      ? (inventories || []).find((inv) => inv && inv.id === selectedInventoryId) || null
      : null;

  // Jump into the masking flow for this source — same entry point as the MaskButton on the
  // masking sources table (route keyed by data-source id; the masking inventory page
  // resolves fields/inventory itself).
  const handleMask = () => {
    const dsId = getDataSourceNumericId(source);
    if (dsId == null) return;
    history.push(`/secure/mask/inventory/${encodeURIComponent(dsId)}`);
  };

  // Download the detail pane's on-screen field grid (including unsaved edits) as CSV or JSON.
  const handleExport = (format) => {
    if (!source) return;
    const baseName =
      (selectedInventory && selectedInventory.name) ||
      (source.name ? `${source.name} masking` : 'inventory');
    const exported = exportInventoryFields(exportDraftRef.current, format, baseName);
    if (exported === 0) {
      setNotification({ header: 'Nothing to export', body: 'This inventory has no fields.' });
    }
  };

  // Head slot: dropdown of this source's named inventories + "New inventory…". Rename /
  // provider / delete stay on the inventory label's right-click menu and the Actions dropdown.
  const inventorySelector = (
    <InventorySelector
      inventories={inventories}
      selectedId={selectedInventoryId === undefined ? null : selectedInventoryId}
      onSelect={(id) => setSelectedInventoryId(id)}
      onNew={() => setSelectedInventoryId(null)}
      disabled={invLoading}
    />
  );

  return (
    <div className='im-page-root'>
      <div className='im-back-row'>
        <Button
          variant='link'
          size='sm'
          className='im-back'
          onClick={() => history.push('/inventory-manager')}
          data-testid='im-back'
        >
          <BsArrowLeft className='mr-1' />
          Back to data sources
        </Button>
      </div>
      <PageHeader
        header='Inventory Manager'
        icon={BsJournalBookmark}
        tooltip='Review and edit the masking inventories of this data source.'
        eyebrow={source ? source.name : undefined}
        actions={[
          <Dropdown.Item
            key='mask'
            disabled={!source}
            onClick={handleMask}
            data-testid='im-mask'
          >
            <FaMask className='mr-2' />
            Mask
          </Dropdown.Item>,
          <Dropdown.Divider key='divider-mask' />,
          <Dropdown.Item
            key='export-csv'
            disabled={!source}
            onClick={() => handleExport('csv')}
            data-testid='im-export-csv'
          >
            <BsDownload className='mr-2' />
            Export CSV
          </Dropdown.Item>,
          <Dropdown.Item
            key='export-json'
            disabled={!source}
            onClick={() => handleExport('json')}
            data-testid='im-export-json'
          >
            <BsDownload className='mr-2' />
            Export JSON
          </Dropdown.Item>,
          <Dropdown.Divider key='divider' />,
          <Dropdown.Item
            key='new-inventory'
            disabled={!source || selectedInventoryId == null || invLoading}
            onClick={() => setSelectedInventoryId(null)}
            data-testid='im-inventory-add'
          >
            <BsPlus className='mr-2' />
            New inventory
          </Dropdown.Item>,
          <Dropdown.Item
            key='delete-inventory'
            className='text-danger'
            disabled={!selectedInventory}
            onClick={() => handleDeleteInventory(selectedInventory)}
            data-testid='im-inventory-delete'
          >
            <BsTrash className='mr-2' />
            Delete inventory
          </Dropdown.Item>,
        ]}
      />
      <Card>
        <div className='im-shell'>
          {source ? (
            <SourceDetail
              key={source.id}
              source={source}
              inventories={inventories}
              selectedInventoryId={selectedInventoryId}
              setSelectedInventoryId={setSelectedInventoryId}
              refreshInventories={refreshInventories}
              onInventoryContextMenu={openInventoryMenu}
              exportDraftRef={exportDraftRef}
              algorithms={algorithms}
              providers={providers}
              defaultEntityAlgorithmMap={entityAlgorithmMap}
              reloadKey={detailReloadKey}
              inventorySelector={inventorySelector}
            />
          ) : (
            <div className='im-detail im-detail-placeholder'>
              {(connections || []).length === 0
                ? 'Loading…'
                : 'Data source not found. It may have been removed, or it is not eligible for masking.'}
            </div>
          )}
          <InventoryContextMenu
            menu={ctxMenu}
            providers={providers}
            currentCheck={<span className='im-ctx-check'>✓</span>}
            onRename={(inv) => setRenameTarget(inv)}
            onSelectProvider={handleSelectProvider}
            onDelete={handleDeleteInventory}
            onClose={() => setCtxMenu(null)}
          />
        </div>
      </Card>
      <InventoryNameModal
        show={renameTarget != null}
        title='Rename inventory'
        initialName={renameTarget ? renameTarget.name || '' : ''}
        submitLabel='Rename'
        onSubmit={handleRenameSubmit}
        onCancel={() => setRenameTarget(null)}
      />
    </div>
  );
}

export default InventorySourcePage;
